import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import "./MyServiceRequests.css";

const STATUS_META = {
  pending: { label: "Pending", tone: "pending" },
  "in-progress": { label: "In progress", tone: "progress" },
  inprogress: { label: "In progress", tone: "progress" },
  approved: { label: "Approved", tone: "progress" },
  resolved: { label: "Resolved", tone: "resolved" },
  completed: { label: "Resolved", tone: "resolved" },
  rejected: { label: "Rejected", tone: "rejected" },
  cancelled: { label: "Cancelled", tone: "rejected" },
};

const getStatusMeta = (status) => {
  const key = (status || "").toLowerCase().replace(/\s+/g, "-");
  return STATUS_META[key] || { label: status || "Unknown", tone: "pending" };
};

const MyServiceRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await api.get("/service-requests/my");
      setRequests(res.data.data || []);
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const statusCounts = useMemo(() => {
    const counts = { all: requests.length };
    requests.forEach((r) => {
      const tone = getStatusMeta(r.status).tone;
      counts[tone] = (counts[tone] || 0) + 1;
    });
    return counts;
  }, [requests]);

  const filteredRequests = useMemo(() => {
    return requests.filter((r) => {
      const matchesStatus =
        statusFilter === "all" || getStatusMeta(r.status).tone === statusFilter;
      const matchesQuery =
        !query.trim() ||
        r.title?.toLowerCase().includes(query.toLowerCase()) ||
        r.category?.toLowerCase().includes(query.toLowerCase());
      return matchesStatus && matchesQuery;
    });
  }, [requests, query, statusFilter]);

  const filterTabs = [
    { key: "all", label: "All" },
    { key: "pending", label: "Pending" },
    { key: "resolved", label: "Resolved" },

  ];

  return (
    <div className="msr-page">
      <div className="msr-container">
        <button className="msr-back" onClick={() => navigate("/dashboard")}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 18l-6-6 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Back to dashboard
        </button>

        <div className="msr-heading">
          <div>
            <h2>My service requests</h2>
            <p className="msr-subtitle">
              {loading
                ? "Loading your requests…"
                : `${requests.length} request${requests.length === 1 ? "" : "s"} on file`}
            </p>
          </div>
        </div>

        {!loading && requests.length > 0 && (
          <div className="msr-toolbar">
            <div className="msr-search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                <path d="M21 21l-4.3-4.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                placeholder="Search by title or category"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            <div className="msr-tabs">
              {filterTabs.map((tab) => (
                <button
                  key={tab.key}
                  className={`msr-tab ${statusFilter === tab.key ? "is-active" : ""}`}
                  onClick={() => setStatusFilter(tab.key)}
                >
                  {tab.label}
                  {statusCounts[tab.key] ? (
                    <span className="msr-tab-count">{statusCounts[tab.key]}</span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="msr-list">
            {[0, 1, 2].map((i) => (
              <div className="msr-skeleton" key={i}>
                <div className="msr-skeleton-spine" />
                <div className="msr-skeleton-body">
                  <div className="msr-skeleton-line" style={{ width: "45%" }} />
                  <div className="msr-skeleton-line" style={{ width: "25%" }} />
                  <div className="msr-skeleton-line" style={{ width: "80%" }} />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="msr-empty">
            <div className="msr-empty-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
                <path d="M12 8v5M12 16h.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </div>
            <h3>Couldn't load your requests</h3>
            <p>Check your connection and try again.</p>
            <button className="msr-retry" onClick={fetchRequests}>Retry</button>
          </div>
        ) : requests.length === 0 ? (
          <div className="msr-empty">
            <div className="msr-empty-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 12h6M9 16h6M9 8h6M6 4h12a1 1 0 011 1v14a1 1 0 01-1 1H6a1 1 0 01-1-1V5a1 1 0 011-1z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h3>No service requests yet</h3>
            <p>Requests you submit will show up here.</p>
            <button className="msr-retry" onClick={() => navigate("/dashboard")}>
              Create a request
            </button>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="msr-empty msr-empty-compact">
            <h3>No matches</h3>
            <p>Try a different search term or filter.</p>
          </div>
        ) : (
          <div className="msr-list">
            {filteredRequests.map((request) => {
              const meta = getStatusMeta(request.status);
              return (
                <div
                  key={request.id}
                  className={`msr-ticket tone-${meta.tone}`}
                  onClick={() => navigate(`/service-request/${request.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && navigate(`/request/${request.id}`)}
                >
                  <div className="msr-ticket-spine" aria-hidden="true" />

                  <div className="msr-ticket-body">
                    <div className="msr-ticket-top">
                      <span className="msr-ticket-id">
                        #{String(request.id).slice(-6).toUpperCase()}
                      </span>
                      <span className={`msr-badge tone-${meta.tone}`}>{meta.label}</span>
                    </div>

                    <h4 className="msr-ticket-title">{request.title}</h4>

                    {request.category && (
                      <span className="msr-ticket-category">{request.category}</span>
                    )}

                    {request.description && (
                      <p className="msr-ticket-desc">{request.description}</p>
                    )}

                    <div className="msr-ticket-footer">
                      <span className="msr-ticket-date">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
                          <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                        {formatDate(request.createdAt)}
                      </span>
                      <span className="msr-ticket-arrow">
                        View details
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyServiceRequests;